/**
 * Add Child Page
 * Form to create a new child profile
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { ArrowLeft, UserPlus, Calendar, GraduationCap, CheckCircle } from 'lucide-react';
import { useSoundEffects } from '../hooks/useSoundEffects';
import { createChild } from '../services/childService';
import { getErrorMessage } from '../services/api';

const addChildSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  gradeLevel: z.number().min(0).max(12),
  parentalConsent: z.boolean().refine(val => val === true, {
    message: 'You must provide parental consent',
  }),
});

type AddChildForm = z.infer<typeof addChildSchema>;

export default function AddChildPage() {
  const navigate = useNavigate();
  const { play } = useSoundEffects();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AddChildForm>({
    resolver: zodResolver(addChildSchema),
    defaultValues: {
      gradeLevel: 1,
      parentalConsent: false,
    },
  });

  const onSubmit = async (data: AddChildForm) => {
    try {
      setError('');
      await createChild({
        name: data.name,
        dateOfBirth: data.dateOfBirth,
        gradeLevel: data.gradeLevel,
        parentalConsent: data.parentalConsent,
      });
      setSuccess(true);
      play('rewardSparkle');
      setTimeout(() => navigate('/parent'), 2000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Link
          to="/parent"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
      >
        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Child Added!</h3>
            <p className="text-gray-600 dark:text-gray-400">Redirecting to dashboard...</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-kid-ocean/10 rounded-xl flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-kid-ocean" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Add Child</h1>
                <p className="text-gray-600 dark:text-gray-400">Create a new child profile</p>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 rounded-xl"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Child's Name
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-kid-ocean focus:border-transparent"
                  placeholder="Enter child's name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Date of Birth
                </label>
                <input
                  type="date"
                  {...register('dateOfBirth')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-kid-ocean focus:border-transparent"
                />
                {errors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-500">{errors.dateOfBirth.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <GraduationCap className="w-4 h-4 inline mr-1" />
                  Grade Level
                </label>
                <select
                  {...register('gradeLevel', { valueAsNumber: true })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-kid-ocean focus:border-transparent"
                >
                  <option value={0}>Kindergarten</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((grade) => (
                    <option key={grade} value={grade}>
                      Grade {grade}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('parentalConsent')}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-kid-ocean focus:ring-kid-ocean"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Parental Consent (COPPA Compliance)
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      I am the parent or legal guardian of this child and I consent to their use of this educational platform. I understand that their conversations will be monitored for safety.
                    </p>
                  </div>
                </label>
                {errors.parentalConsent && (
                  <p className="mt-2 text-sm text-red-500">{errors.parentalConsent.message}</p>
                )}
              </div>

              <div className="flex gap-4">
                <Link
                  to="/parent"
                  className="flex-1 px-6 py-3 text-center border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-kid-ocean text-white rounded-xl hover:bg-kid-ocean/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Adding...' : 'Add Child'}
                </button>
              </div>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
